import { Color, Vector3 } from 'three';

import { Conic, getConicLength } from '../design/design-node/utils/conic';
import { isStripeDrawnAt, Stripe, StripePattern } from './styled-segment';

/**
 * Each item (a straight segment, a curve, a dot or a triangle) is 16 floats, 4 texels of the item
 * texture that the shader reads:
 *
 * |   | x       | y       | z       | w                                         |
 * |---|---------|---------|---------|-------------------------------------------|
 * | 0 | start                     | | kind: curve weight (> 0), straight (0), triangle (-1) |
 * | 1 | end                       | | width (0 is thin)                         |
 * | 2 | control point, or the third corner of a triangle | | color, 8-bit sRGB packed as r·65536 + g·256 + b |
 * | 3 | stripe pattern (-1 is solid) | stripe scale | stripe offset | alpha |
 *
 * A straight segment of length 0 is a dot of the width.
 */
export const floatsPerItem = 16;

/**
 * The items are laid out in rows of the item texture.
 */
export const itemsPerRow = 1024;

/**
 * The maximum number of items in one StyledLines, so that the item texture is at most 4096 x 1024.
 */
export const maxItems = itemsPerRow * 1024;

export const straightKind = 0;
export const triangleKind = -1;

/**
 * The number of straight steps a curve is drawn with at most. Fewer are drawn when it is small on
 * screen, a power of two that this is divisible by, see StyledLines.updateCurveSteps.
 */
export const curveSteps = 32;

/**
 * The items to draw, in the order to draw them.
 */
export type StyledLinesData = {
  items: Float32Array;
  /** Returned as the index of intersections with the item, -1 for joins. */
  ids: Int32Array;
  /** The layer of each item, an index into StyledLines.layerObjects, which can hide it. */
  layers: Uint32Array;
  count: number;
  patterns: StripePattern[];
  /**
   * The positions (x, y, z) where curves are only split into pieces, e.g. the quarter arcs of a
   * circle, see skymap-oda's shared/cpp/glb/README.md (Seams). Not drawn, but kept for creating
   * design geometry from the items.
   */
  seams: Float32Array;
};

export const emptyStyledLinesData = (patterns: StripePattern[] = []): StyledLinesData => ({
  items: new Float32Array(),
  ids: new Int32Array(),
  layers: new Uint32Array(),
  count: 0,
  patterns,
  seams: new Float32Array(),
});

export type LineStyle = {
  width?: number;
  stripe?: Stripe;
  color: Color;
  alpha?: number;
  id: number;
};

/**
 * Identifies the vertex at an end of a segment, so that segments that share a vertex are joined.
 */
export type JoinKey = number | string;

type SegmentEnd = { key: JoinKey; position: Vector3; width: number; style: LineStyle };

/**
 * Builds the data of styled lines in the order the items are added. Lines with a width are joined
 * where they share a vertex, with a dot of the width in each group of items (e.g. a primitive).
 * Splits the items into chunks of at most maxItems.
 */
export class StyledLinesBuilder {
  private chunks: StyledLinesData[] = [];
  private items = new Float32Array(floatsPerItem * 256);
  private ids = new Int32Array(256);
  private layers = new Uint32Array(256);
  private count = 0;
  private layer = 0;
  private ends: SegmentEnd[] = [];
  private seams: number[] = [];

  constructor(private readonly patterns: StripePattern[]) {}

  get itemCount() {
    return this.chunks.reduce((sum, x) => sum + x.count, 0) + this.count;
  }

  /**
   * Sets the layer of the items added from now on, see StyledLinesData.
   */
  setLayer(layer: number) {
    this.layer = layer;
  }

  addSegment(start: Vector3, end: Vector3, style: LineStyle, joinKeys?: [JoinKey, JoinKey]) {
    this.addItem(start, end, end, straightKind, style);
    this.addEnds(start, end, style, joinKeys, () => start.distanceTo(end));
  }

  addCurve(conic: Conic, style: LineStyle, joinKeys?: [JoinKey, JoinKey]) {
    this.addItem(conic.start, conic.end, conic.control, conic.weight, style);
    this.addEnds(conic.start, conic.end, style, joinKeys, () => getConicLength(conic));
  }

  /**
   * Adds a position where a curve is only split into pieces, see StyledLinesData.
   */
  addSeam(position: Vector3) {
    this.seams.push(position.x, position.y, position.z);
  }

  addTriangle(
    a: Vector3,
    b: Vector3,
    c: Vector3,
    style: Pick<LineStyle, 'color' | 'alpha' | 'id'>,
  ) {
    this.addItem(a, b, c, triangleKind, style);
  }

  /**
   * Ends a group of items, e.g. a primitive, and adds the joins between its segments.
   */
  endGroup() {
    const vertices = new Map<JoinKey, SegmentEnd[]>();

    for (const end of this.ends) {
      const meeting = vertices.get(end.key);

      if (meeting) {
        meeting.push(end);
      } else {
        vertices.set(end.key, [end]);
      }
    }

    this.ends = [];

    for (const meeting of vertices.values()) {
      if (meeting.length < 2) {
        continue;
      }

      // As wide as the second widest, so that the dot doesn't stick out of the narrower lines.
      const [, second] = [...meeting].sort((a, b) => b.width - a.width);
      const position = second.position;
      this.addItem(position, position, position, straightKind, {
        ...second.style,
        id: -1,
        stripe: undefined,
      });
    }
  }

  /**
   * Gets the data, in chunks of at most maxItems.
   */
  build(): StyledLinesData[] {
    this.endGroup();
    this.finishChunk();
    const chunks = this.chunks;
    this.chunks = [];
    return chunks;
  }

  private addEnds(
    start: Vector3,
    end: Vector3,
    style: LineStyle,
    joinKeys: [JoinKey, JoinKey] | undefined,
    getLength: () => number,
  ) {
    const width = style.width ?? 0;

    if (width <= 0 || start.equals(end)) {
      return;
    }

    const drawnAt = (d: number) =>
      !style.stripe || isStripeDrawnAt(style.stripe, this.patterns[style.stripe.pattern] ?? [], d);
    const keyOf = (position: Vector3) => `${position.x},${position.y},${position.z}`;

    if (drawnAt(0)) {
      this.ends.push({ key: joinKeys?.[0] ?? keyOf(start), position: start, width, style });
    }

    // Just before the end, since the stripe's next length starts at the end.
    if (!style.stripe || drawnAt(getLength() * (1 - 1e-9))) {
      this.ends.push({ key: joinKeys?.[1] ?? keyOf(end), position: end, width, style });
    }
  }

  private addItem(
    start: Vector3,
    end: Vector3,
    control: Vector3,
    kind: number,
    { width, stripe, color, alpha, id }: LineStyle,
  ) {
    if (this.count === maxItems) {
      this.finishChunk();
    }

    if (this.count === this.ids.length) {
      const items = new Float32Array(this.items.length * 2);
      items.set(this.items);
      this.items = items;
      const ids = new Int32Array(this.ids.length * 2);
      ids.set(this.ids);
      this.ids = ids;
      const layers = new Uint32Array(this.layers.length * 2);
      layers.set(this.layers);
      this.layers = layers;
    }

    const offset = this.count * floatsPerItem;
    const items = this.items;

    items[offset] = start.x;
    items[offset + 1] = start.y;
    items[offset + 2] = start.z;
    items[offset + 3] = kind;
    items[offset + 4] = end.x;
    items[offset + 5] = end.y;
    items[offset + 6] = end.z;
    items[offset + 7] = width ?? 0;
    items[offset + 8] = control.x;
    items[offset + 9] = control.y;
    items[offset + 10] = control.z;
    // getHex is in sRGB, which keeps 8 bits per channel even for dark colors.
    items[offset + 11] = color.getHex();
    items[offset + 12] = stripe ? stripe.pattern : -1;
    items[offset + 13] = stripe ? stripe.scale : 1;
    items[offset + 14] = stripe ? stripe.offset : 0;
    items[offset + 15] = alpha ?? 1;

    this.ids[this.count] = id;
    this.layers[this.count] = this.layer;
    this.count++;
  }

  private finishChunk() {
    if (this.count === 0) {
      return;
    }

    this.chunks.push({
      items: this.items.slice(0, this.count * floatsPerItem),
      ids: this.ids.slice(0, this.count),
      layers: this.layers.slice(0, this.count),
      count: this.count,
      patterns: this.patterns,
      seams: new Float32Array(this.seams),
    });

    this.items = new Float32Array(floatsPerItem * 256);
    this.ids = new Int32Array(256);
    this.layers = new Uint32Array(256);
    this.count = 0;
  }
}

/**
 * Reads an item, e.g. for intersections or for creating design geometry from it.
 */
export function readItem(data: StyledLinesData, index: number) {
  const offset = index * floatsPerItem;
  const items = data.items;
  const vector = (i: number) =>
    new Vector3(items[offset + i], items[offset + i + 1], items[offset + i + 2]);
  const kind = items[offset + 3];
  const pattern = items[offset + 12];

  return {
    kind:
      kind > 0
        ? ('curve' as const)
        : kind === triangleKind
          ? ('triangle' as const)
          : ('straight' as const),
    start: vector(0),
    end: vector(4),
    /** The control point of a curve, or the third corner of a triangle. */
    control: vector(8),
    weight: kind,
    width: items[offset + 7],
    stripe:
      pattern >= 0 ? { pattern, scale: items[offset + 13], offset: items[offset + 14] } : undefined,
    color: new Color(items[offset + 11]),
    alpha: items[offset + 15],
    id: data.ids[index],
  };
}

export type StyledItem = ReturnType<typeof readItem>;
